<template>
  <vdl-form-field
    :title="title"
    tabindex="-1"
    :class="{
      'type-autocomplete': true,
      'field-item-text': true,
      'active-field-autocomplete': inputValue && inputValue.length > 0 && change_when_active,
    }"
  >
    <md-icon v-if="svgIcon" :mdSvg="svgIcon" :fillRule="fillRule" :size="iconSize" :class="{ 'md-new-tag': newTag }" />
    <div class="autocomplete-container">
      <div
        class="md-list-text-container"
        :class="{
          'md-title-tag-parameters': titleTag,
          'md-input-invalid': errors && errors.length && !disableValidation,
        }"
      >
        <tdl-typeahead
          v-model="inputValue"
          :only-chips="only_chips"
          :only-regular-characters="onlyRegularCharacters"
          :fetch-list="fetchListForField(field)"
          :excluded-list="field['excludedValues'] || []"
          :excluded-message="excludedMessage"
          :input-label="title ? '' : name"
          :text-attribute="textAttribute"
          :item-list-limit="Infinity"
          :max-items="maxItems || 10"
          :item-list-show="4"
          :min-chars="minChars"
          :debounce="0"
          :soft-title="softTitle"
          :disable-max-width="disableMaxWidth"
          :soft-list="softList"
          :add-new-message="addNewMessage"
          :add-new-item="addNewItem"
          :desktop-item-list-fixed="true"
          :enabledScroll="enabledScroll"
          :desktop-search-hint-mode="'focus'"
          cancel-label="cancel"
          :force-close="forceClose"
          :id-attribute="field.idAttribute"
          :grouped="grouped"
          :disable-auto-focus="disableAutoFocus"
          :empty-values-message="emptyValuesMessage"
          @addItem="$emit('change', field)"
          @removeItem="$emit('change', field)"
          :fetch-next-page="fetchNextPage"
          :id="elementId(field)"
        >
        </tdl-typeahead>
        <span
          :class="{ 'md-count': true, 'md-count-error': (inputValue ? inputValue.length : 0) > maxItems }"
          v-if="enableCounter && maxItems"
          >{{ inputValue ? inputValue.length : 0 }} / {{ maxItems }}</span
        >
        <span class="field-caption" v-for="captionItem in captionItems" :key="captionItem">
          <span v-for="innerCaption in spaces(captionItem)" :key="innerCaption">
            <template v-if="innerCaption.type === 'tooltip'">
              <span class="tooltip-auto">
                <md-icon :mdSvg="mdiInformationOutline" />
                <div class="banner-auto">
                  {{ innerCaption.value }}
                </div>
              </span>
            </template>
            <span v-else v-html="innerCaption.value"></span>
          </span>
        </span>
        <span class="form-error autocomplete-error">{{ errors && errors[0] }}</span>
      </div>
      <div
        class="operator-wrapper"
        v-bind:class="{ 'md-inset': !field.noInsetNested }"
        v-show="showOperator(field) && field.nested"
      >
        <slot />
      </div>
    </div>
    <template #conditionalNested>
      <div v-show="showConditionalNested">
        <slot name="conditionalNested" />
      </div>
    </template>
  </vdl-form-field>
</template>
<script lang="ts">
import { isUnderMobileWidth } from '../../../core/browser';
import { Options as Component, Prop, mixins } from 'vue-property-decorator';

import FormGeneratorField from '../FormGeneratorIconizedField';
import { mdiInformationOutline } from '@mdi/js';

@Component({
  components: {},
})
export default class AutocompleteField extends mixins(FormGeneratorField) {
  mdiInformationOutline = mdiInformationOutline;
  @Prop()
  maxItems: number;

  @Prop({ default: false })
  disableValidation: boolean;

  @Prop({ default: false })
  onlyRegularCharacters: boolean;

  @Prop({ default: 'name' })
  textAttribute: string;

  @Prop({ default: false })
  change_when_active: boolean;

  @Prop({ default: false })
  newTag: boolean;

  @Prop({ default: false })
  titleTag: boolean;

  @Prop({ default: false })
  enabledScroll: boolean;

  @Prop({ default: false })
  enableCounter: boolean;

  @Prop({ default: 0 })
  minChars: number;

  @Prop({ default: 'Be the first one to add this' })
  addNewMessage: string;

  @Prop({ default: false })
  disableMaxWidth: boolean;

  @Prop({ default: false })
  only_chips: boolean;

  @Prop()
  caption: any;

  @Prop()
  mobileCaption: any;

  @Prop({ default: false })
  addNewItem: boolean;

  @Prop({ default: false })
  onlyLetters: boolean;

  @Prop({ default: false })
  softList: boolean;

  @Prop({ default: false })
  grouped: boolean;

  @Prop({ default: false })
  disableAutoFocus: boolean;

  @Prop({ default: 'Word unavailable in this field. Try again.' })
  excludedMessage: string;

  @Prop({ default: 'Start typing for keywords...' })
  softTitle: String;

  @Prop()
  emptyValuesMessage: string;

  @Prop()
  fetchList: Function;

  @Prop({ default: (a) => a })
  fetchNextPage: any;

  @Prop()
  conditionalNested: any;

  get captionItems() {
    if (isUnderMobileWidth() && this.mobileCaption && this.mobileCaption.length > 0) {
      return this.mobileCaption;
    }
    return this.caption;
  }

  spaces(text) {
    let spaces = [];
    let word = '';
    for (let i = 0; i < text.length; i++) {
      let letter = text.charAt(i);
      if (letter === '<') {
        const subText = text.slice(i + 1, i + 9);
        if (subText === 'tooltip>') {
          spaces.push({ type: 'text', value: word });
          word = '';
          i += 8;
        } else {
          word += letter;
        }
      } else if (letter === '/') {
        const subText = text.slice(i + 1, i + 9);
        if (subText === 'tooltip>') {
          spaces.push({ type: 'tooltip', value: word });
          word = '';
          i += 8;
        } else {
          word += letter;
        }
      } else {
        word += letter;
      }
    }
    if (word != '') {
      spaces.push({ type: 'text', value: word });
    }
    return spaces;
  }

  fetchListForField(field, fieldName = 'values') {
    const fetchList = (query, initialValue = false) => {
      return new Promise((resolve) => {
        if (query) {
          const matching = field[fieldName].filter((f) => {
            const name = (f[this.field.textAttribute || 'name'] || '').toLowerCase();
            const q = query.toLowerCase();
            return initialValue ? name.indexOf(q) == 0 : name.indexOf(q) >= 0;
          });
          resolve(matching);
        } else {
          resolve(field[fieldName]);
        }
      });
    };

    return this.fetchList instanceof Function ? this.fetchList : fetchList;
  }
}
</script>
<style lang="scss">
@import '../../../vars.scss';

.type-autocomplete {
  .field-caption {
    color: $text-contrast-60 !important;
  }
  .operator-wrapper {
    margin-top: -16px;
  }
  .md-count {
    color: rgba(0, 0, 0, 0.87);
    position: relative;
    margin-top: -16px;
    align-self: flex-end;
    font-weight: 400;
    text-align: left;
    font-size: 12px;
    text-transform: none;
  }

  .md-count-error {
    color: #f44336 !important;
  }

  .tooltip-auto:hover {
    .banner-auto {
      display: block !important;
    }
  }

  .banner-auto {
    float: left;
    width: 248px;
    z-index: 10000;
    position: absolute;
    background-color: rgba(0, 0, 0, 0.54);
    text-align: center !important;
    border-radius: 5px;
    display: none;
    bottom: 35px;
    padding: 2px 8px;
    background-color: #616161de;
    border-radius: 2px;
    transform-origin: center top;
    transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
    transition-duration: 0.3s;
    transition-delay: 0s;
    color: #fff;
    font-family: Muli, Helvetica, Arial, sans-serif;
    font-weight: 600 !important;
    font-size: 12px;
    line-height: 16px;
    text-transform: none;
  }

  .md-list-item-container {
    .md-list-text-container {
      .tdl-typeahead {
        .md-chips {
          min-height: auto;
          margin-top: 8px;
          .md-input {
            min-width: 10%;
          }
          .md-chip {
            margin-right: 8px;
            margin-bottom: 8px;
            @media (max-width: 720px) {
              width: 100%;
            }
           
            .md-delete {
              position: absolute;
            }
          }
        }
      }
    }
  }
}
</style>
